import * as THREE from 'three';
import type {MessageApi} from 'naive-ui';
import { convertMmToM } from '@puppycad/cad-core';
import type {ExtrudeTarget, ExtrudeViewportRef} from './extrude-types';
import {
  buildExtrudeHandleGizmo,
  collectExtrudeHandlePickTargets,
  closestPointOnAxisFromRay,
  computeExtrudeHandleOneSideLength,
  computeSelectedContoursCenter,
  computeWorldSizeFromPixels,
  type ExtrudeGizmoPartKind,
  orientExtrudeHandleGizmo,
  resolveExtrudeGizmoPartKindFromObject,
  updateExtrudeHandleGizmoVisual,
} from './extrude-handle-gizmo';
import type {
  ExtrudeDirectionDragState,
  ExtrudeHandleRole,
  ExtrudeHandleSide,
} from './extrude-direction-drag-state';
import {
  cloneExtrudeDirectionDragState,
  resolveExtrudeDirectionHandleLayout,
} from './extrude-direction-drag-state';

type DragControllerOptions = {
  viewportRef: ExtrudeViewportRef;
  message: MessageApi;
  onPreview: (target: ExtrudeTarget, state: ExtrudeDirectionDragState, force?: boolean) => void;
  onApply: (target: ExtrudeTarget, state: ExtrudeDirectionDragState) => Promise<void>;
  onStateChange?: (state: ExtrudeDirectionDragState) => void;
  /**
   * 判断当前目标在鼠标拖动期间是否暂停预览重建。
   *
   * @param target 拉伸目标。
   * @returns 返回 true 时拖动过程中只更新定义与手柄，鼠标释放后强制提交一次预览。
   */
  shouldSuspendPreviewWhileDragging?: (target: ExtrudeTarget) => boolean;
  /**
   * 通知调用方拖动预览暂停状态发生变化。
   *
   * @param isSuspended 是否已经进入暂停预览状态。
   */
  onDragPreviewSuspensionChange?: (isSuspended: boolean) => void;
  onFinish: () => void;
};

type StartOptions = {
  state: ExtrudeDirectionDragState;
  forcePreview?: boolean;
};

type UpdateTargetOptions = {
  state?: ExtrudeDirectionDragState;
  forcePreview?: boolean;
};

type SyncStateOptions = {
  forcePreview?: boolean;
  emitChange?: boolean;
};

type DragHeadDescriptor = {
  side: ExtrudeHandleSide;
  role: ExtrudeHandleRole;
  kind: ExtrudeGizmoPartKind;
};

type ApplyDragDeltaInput = {
  state: ExtrudeDirectionDragState;
  head: DragHeadDescriptor;
  deltaAlongAxis: number;
};

const MIN_DEPTH = 1e-6;
const HANDLE_HEAD_PIXEL_SIZE = 24;
const HANDLE_SHAFT_RADIUS_PIXEL_SIZE = 4;

//------ 拉伸手柄毫米级适配 开始------
/**
 * 默认拉伸深度。
 *
 * 说明：
 * 1. PropertyManager 中的默认语义是 10 mm；
 * 2. Three.js 场景与 cad-core 求解层仍统一使用米制；
 * 3. 拖拽控制器必须在入口处保存米制值，避免手柄以 10 m 的尺度显示。
 */
const DEFAULT_DEPTH = convertMmToM(10);

/**
 * 拉伸拖拽数值取整倍率。
 *
 * 说明：
 * 1. 底层单位是米，毫米级建模下 0.01 表示 10 mm；
 * 2. 使用 1e6 可以保留到 0.001 mm，避免三位米制取整把小尺寸拖拽压成 0；
 * 3. 该精度只用于交互拖拽状态，不改变最终特征定义的单位规则。
 */
const EXTRUDE_DRAG_DECIMAL_SCALE = 1000000;
//------ 拉伸手柄毫米级适配 结束------

/**
 * 归一化拖拽深度。
 *
 * 说明：
 * 1. 拖拽过程中允许某一侧收缩到 0；
 * 2. 交互层保留到 0.001 mm，避免毫米级拖动被米制粗取整压成 0；
 * 3. 最终提交时再由属性定义层提升到最小有效厚度。
 *
 * @param value 输入值。
 * @returns 归一化后的非负值。
 */
function roundDragDepthValue(value: number): number {
  if (!Number.isFinite(value)) {
    return 0;
  }
  return Math.max(0, Math.round(Math.abs(value) * EXTRUDE_DRAG_DECIMAL_SCALE) / EXTRUDE_DRAG_DECIMAL_SCALE);
}

/**
 * 根据部件类型解析手柄描述。
 *
 * @param partKind 部件类型。
 * @returns 手柄描述；轴体命中时返回 null。
 */
export function resolveDragHeadDescriptor(
  partKind: ExtrudeGizmoPartKind | 'unknown',
): DragHeadDescriptor | null {
  switch (partKind) {
    case 'positivePrimaryHead':
      return { side: 'positive', role: 'primary', kind: partKind };
    case 'negativePrimaryHead':
      return { side: 'negative', role: 'primary', kind: partKind };
    case 'positiveSecondaryHead':
      return { side: 'positive', role: 'secondary', kind: partKind };
    case 'negativeSecondaryHead':
      return { side: 'negative', role: 'secondary', kind: partKind };
    default:
      return null;
  }
}

/**
 * 将轴向拖拽增量应用到方向状态。
 *
 * 说明：
 * 1. 主侧始终控制 Direction 1 深度；
 * 2. 对侧始终控制 Direction 2 深度；
 * 3. 负向侧朝远离草图平面拖动时，轴向投影增量为负，因此这里必须反转符号。
 *
 * @param input 输入参数。
 * @returns 新的拖拽状态。
 */
export function applyDragDeltaToDirectionState(
  input: ApplyDragDeltaInput,
): ExtrudeDirectionDragState {
  const depthDelta = input.head.side === 'positive'
    ? input.deltaAlongAxis
    : -input.deltaAlongAxis;
  const nextState = cloneExtrudeDirectionDragState(input.state);

  if (input.head.role === 'primary') {
    if (nextState.primaryDepthEditable === false) return nextState;
    // 对称手柄移动一侧距离时，总深度同步变化两倍，另一侧由布局统一镜像。
    nextState.direction1Depth = roundDragDepthValue(nextState.direction1Depth + depthDelta * (nextState.isMidPlane ? 2 : 1));
    return nextState;
  }

  if (nextState.secondaryDepthEditable === false) return nextState;
  nextState.direction2Enabled = true;
  nextState.direction2Depth = roundDragDepthValue(nextState.direction2Depth + depthDelta);
  return nextState;
}

/**
 * 根据轴上投影点选择实际拖拽的手柄。
 *
 * @param input 输入参数。
 * @returns 实际参与拖拽的手柄。
 */
export function resolveHeadDescriptorFromProjection(input: {
  projectedOnAxis: number;
  state: ExtrudeDirectionDragState;
  hitPart: ExtrudeGizmoPartKind | 'unknown';
}): DragHeadDescriptor | null {
  const directHead = resolveDragHeadDescriptor(input.hitPart);
  if (directHead) {
    return (directHead.role === 'primary' ? input.state.primaryDepthEditable : input.state.secondaryDepthEditable) === false ? null : directHead;
  }
  if (input.hitPart !== 'shaft') {
    return null;
  }

  const layout = resolveExtrudeDirectionHandleLayout(input.state);
  // 角色决定可见箭头，可编辑权限独立控制点击和轴体拖动。
  if ((input.projectedOnAxis >= 0 ? layout.positiveRole : layout.negativeRole) === 'primary'
    ? input.state.primaryDepthEditable === false : input.state.secondaryDepthEditable === false) return null;
  if (input.projectedOnAxis > MIN_DEPTH && layout.positiveRole) {
    return {
      side: 'positive',
      role: layout.positiveRole,
      kind: layout.positiveRole === 'primary' ? 'positivePrimaryHead' : 'positiveSecondaryHead',
    };
  }
  if (input.projectedOnAxis < -MIN_DEPTH && layout.negativeRole) {
    return {
      side: 'negative',
      role: layout.negativeRole,
      kind: layout.negativeRole === 'primary' ? 'negativePrimaryHead' : 'negativeSecondaryHead',
    };
  }
  if (layout.primarySide === 'positive') {
    return layout.positiveRole
      ? {
        side: 'positive',
        role: layout.positiveRole,
        kind: layout.positiveRole === 'primary' ? 'positivePrimaryHead' : 'positiveSecondaryHead',
      }
      : null;
  }
  return layout.negativeRole
    ? {
      side: 'negative',
      role: layout.negativeRole,
      kind: layout.negativeRole === 'primary' ? 'negativePrimaryHead' : 'negativeSecondaryHead',
    }
    : null;
}

/**
 * 创建拉伸拖拽控制器。
 *
 * @param options 控制器配置。
 * @returns 控制器实例。
 */
export function createExtrudeDragController(options: DragControllerOptions) {
  const {
    viewportRef,
    message,
    onPreview,
    onApply,
    onStateChange,
    shouldSuspendPreviewWhileDragging,
    onDragPreviewSuspensionChange,
    onFinish,
  } = options;
  const raycaster = new THREE.Raycaster();
  const mouse = new THREE.Vector2();

  let activeTarget: ExtrudeTarget | null = null;
  let activeContainer: HTMLElement | null = null;
  let activeCamera: THREE.Camera | null = null;
  let activeScene: THREE.Scene | null = null;
  let gizmo: THREE.Group | null = null;
  let currentState: ExtrudeDirectionDragState = {
    direction1Depth: DEFAULT_DEPTH,
    direction1Reverse: false,
    direction2Enabled: false,
    direction2Depth: DEFAULT_DEPTH,
  };
  let isDragging = false;
  let isDragPreviewSuspended = false;
  let dragStartHead: DragHeadDescriptor | null = null;
  let dragStartProjected = 0;
  let dragStartState = cloneExtrudeDirectionDragState(currentState);
  let previousCursor: string | null = null;

  /**
   * 对外分发状态变化。
   */
  const emitStateChange = () => {
    onStateChange?.(cloneExtrudeDirectionDragState(currentState));
  };

  /**
   * 刷新预览。
   *
   * @param force 是否强制刷新。
   */
  const refreshPreview = (force = false) => {
    if (!activeTarget) {
      return;
    }
    onPreview(activeTarget, cloneExtrudeDirectionDragState(currentState), force);
  };

  /**
   * 更新鼠标 NDC。
   *
   * @param event 鼠标事件。
   * @param container 容器。
   */
  const updateMouse = (event: MouseEvent, container: HTMLElement) => {
    const rect = container.getBoundingClientRect();
    mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
  };

  /**
   * 设置鼠标样式。
   *
   * @param cursor 光标样式。
   */
  const setCursor = (cursor: string | null) => {
    if (!activeContainer) {
      return;
    }
    if (cursor) {
      activeContainer.style.cursor = cursor;
      return;
    }
    if (previousCursor !== null) {
      activeContainer.style.cursor = previousCursor;
    }
  };

  /**
   * 计算手柄中心。
   *
   * @param target 拉伸目标。
   * @returns 手柄中心。
   */
  const computeHandleCenter = (target: ExtrudeTarget): THREE.Vector3 => {
    if (Array.isArray(target.regions) && target.regions.length > 0) {
      const center = computeSelectedContoursCenter(target.regions.map((item) => item.geometry));
      if (center.lengthSq() > 0) {
        return center;
      }
    }
    return target.basePoint.clone();
  };

  /**
   * 解析当前手柄命中。
   *
   * @param event 鼠标事件。
   * @returns 命中结果。
   */
  const resolveGizmoIntersections = (event: MouseEvent): THREE.Intersection[] => {
    if (!activeContainer || !activeCamera || !gizmo) {
      return [];
    }
    updateMouse(event, activeContainer);
    raycaster.setFromCamera(mouse, activeCamera);
    const pickTargets = collectExtrudeHandlePickTargets(gizmo);
    const center = activeTarget ? computeHandleCenter(activeTarget) : null;
    return raycaster.intersectObjects(pickTargets, true).filter((hit) => center && activeTarget && resolveHeadDescriptorFromProjection({
      hitPart: resolveExtrudeGizmoPartKindFromObject(hit.object),
      projectedOnAxis: hit.point.clone().sub(center).dot(activeTarget.normal),
      state: currentState,
    }));
  };

  /**
   * 解析主命中结果。
   *
   * @param hits 命中集合。
   * @returns 主命中。
   */
  const resolvePrimaryGizmoHit = (
    hits: THREE.Intersection[],
  ): THREE.Intersection | null => {
    if (hits.length === 0) {
      return null;
    }
    const withPart = hits.map((hit) => ({
      hit,
      part: resolveExtrudeGizmoPartKindFromObject(hit.object),
    }));
    const priorityByPart = (part: ExtrudeGizmoPartKind | 'unknown'): number => {
      if (part === 'shaft') {
        return 1;
      }
      if (part === 'unknown') {
        return 0;
      }
      return 2;
    };
    withPart.sort((left, right) => {
      const priorityDelta = priorityByPart(right.part) - priorityByPart(left.part);
      if (priorityDelta !== 0) {
        return priorityDelta;
      }
      return left.hit.distance - right.hit.distance;
    });
    return withPart[0]?.hit ?? null;
  };

  /** 最近有效预览的轴向显示范围；不参与几何终止求值。 */
  let previewLengths: [number, number] = [0, 0];
  /** 更新手柄几何位置。 */
  const updateGizmoTransform = () => {
    if (!activeTarget || !activeCamera || !activeContainer || !gizmo) {
      return;
    }
    const center = computeHandleCenter(activeTarget);
    orientExtrudeHandleGizmo(gizmo, center, activeTarget.normal);

    const headSize = computeWorldSizeFromPixels(
      activeCamera,
      activeContainer,
      center,
      HANDLE_HEAD_PIXEL_SIZE,
    );
    const shaftRadius = Math.max(
      1e-4,
      computeWorldSizeFromPixels(
        activeCamera,
        activeContainer,
        center,
        HANDLE_SHAFT_RADIUS_PIXEL_SIZE,
      ),
    );
    const layout = resolveExtrudeDirectionHandleLayout(currentState, previewLengths);
    updateExtrudeHandleGizmoVisual(gizmo, {
      positiveLength: layout.positiveLength > 0
        ? computeExtrudeHandleOneSideLength(layout.positiveLength)
        : 0,
      negativeLength: layout.negativeLength > 0
        ? computeExtrudeHandleOneSideLength(layout.negativeLength)
        : 0,
      positiveRole: layout.positiveRole,
      negativeRole: layout.negativeRole,
      shaftRadius,
      headSize,
    });
  };

  /**
   * 将真实预览顶点投影到草图法向，更新只读箭头；不触发预览或回写定义。
   * @param positions 当前有效预览顶点；null 清理上一请求的显示范围。
   */
  const syncPreviewPositions = (positions: ArrayLike<number> | null): void => {
    previewLengths = [0, 0];
    if (positions && activeTarget) {
      const center = computeHandleCenter(activeTarget);
      const axis = activeTarget.normal.clone().normalize();
      for (let index = 0; index + 2 < positions.length; index += 3) {
        const projected = (positions[index] - center.x) * axis.x + (positions[index + 1] - center.y) * axis.y + (positions[index + 2] - center.z) * axis.z;
        previewLengths[0] = Math.max(previewLengths[0], projected);
        previewLengths[1] = Math.max(previewLengths[1], -projected);
      }
    }
    updateGizmoTransform();
  };

  /** 确保可视手柄存在。 */
  const ensureGizmo = () => {
    if (!activeScene) {
      return;
    }
    if (!gizmo) {
      gizmo = buildExtrudeHandleGizmo();
      options.viewportRef.value?.addObject(gizmo, activeScene);
    }
    updateGizmoTransform();
  };

  /**
   * 销毁当前手柄。
   */
  const disposeGizmo = () => {
    if (!gizmo) {
      return;
    }
    if (gizmo.parent) {
      gizmo.parent.remove(gizmo);
    }
    gizmo.traverse((object) => {
      if (!(object instanceof THREE.Mesh)) {
        return;
      }
      object.geometry.dispose();
      const material = object.material;
      if (Array.isArray(material)) {
        material.forEach((item) => item.dispose());
      } else if (material) {
        material.dispose();
      }
    });
    gizmo = null;
  };

  /**
   * 同步状态。
   *
   * @param nextState 新状态。
   * @param options 同步选项。
   * @returns 是否成功。
   */
  const syncState = (nextState: ExtrudeDirectionDragState, options: SyncStateOptions = {}): boolean => {
    currentState = cloneExtrudeDirectionDragState(nextState);
    if (options.emitChange !== false) {
      emitStateChange();
    }
    updateGizmoTransform();
    refreshPreview(options.forcePreview ?? true);
    return true;
  };

  /**
   * 从鼠标事件开始拖拽。
   *
   * @param event 鼠标事件。
   * @returns 是否成功开始。
   */
  const startDragFromEvent = (event: MouseEvent): boolean => {
    if (!activeTarget || event.button !== 0) {
      return false;
    }
    const hits = resolveGizmoIntersections(event);
    if (hits.length === 0) {
      return false;
    }
    const firstHit = resolvePrimaryGizmoHit(hits);
    if (!firstHit) {
      return false;
    }
    const center = computeHandleCenter(activeTarget);
    const axisDirection = activeTarget.normal.clone().normalize();
    const hitPartKind = resolveExtrudeGizmoPartKindFromObject(firstHit.object);
    const hitProjected = firstHit.point.clone().sub(center).dot(axisDirection);
    const resolvedHead = resolveHeadDescriptorFromProjection({
      hitPart: hitPartKind,
      projectedOnAxis: hitProjected,
      state: currentState,
    });
    if (!resolvedHead) {
      return false;
    }
    const pointOnAxis = closestPointOnAxisFromRay(
      raycaster.ray,
      center,
      activeTarget.normal,
    );
    dragStartHead = resolvedHead;
    dragStartProjected = pointOnAxis.clone().sub(center).dot(axisDirection);
    dragStartState = cloneExtrudeDirectionDragState(currentState);
    isDragging = true;
    isDragPreviewSuspended = shouldSuspendPreviewWhileDragging?.(activeTarget) === true;
    if (isDragPreviewSuspended) {
      onDragPreviewSuspensionChange?.(true);
    }
    setCursor('grabbing');
    event.preventDefault();
    event.stopPropagation();
    return true;
  };

  /**
   * 根据鼠标事件更新拖拽。
   *
   * @param event 鼠标事件。
   */
  const updateDragByEvent = (event: MouseEvent): void => {
    if (!isDragging || !activeTarget || !activeContainer || !activeCamera || !dragStartHead) {
      return;
    }
    updateMouse(event, activeContainer);
    raycaster.setFromCamera(mouse, activeCamera);
    const center = computeHandleCenter(activeTarget);
    const axisDirection = activeTarget.normal.clone().normalize();
    const pointOnAxis = closestPointOnAxisFromRay(
      raycaster.ray,
      center,
      activeTarget.normal,
    );
    const currentProjected = pointOnAxis.clone().sub(center).dot(axisDirection);
    const deltaAlongAxis = currentProjected - dragStartProjected;
    currentState = applyDragDeltaToDirectionState({
      state: dragStartState,
      head: dragStartHead,
      deltaAlongAxis,
    });
    emitStateChange();
    if (!isDragPreviewSuspended) {
      refreshPreview();
    }
    updateGizmoTransform();
    event.preventDefault();
    event.stopPropagation();
  };

  /**
   * 停止拖拽。
   */
  const stopDrag = () => {
    const shouldFlushPreview = isDragPreviewSuspended && activeTarget !== null;
    isDragging = false;
    isDragPreviewSuspended = false;
    if (shouldFlushPreview) {
      onDragPreviewSuspensionChange?.(false);
    }
    dragStartHead = null;
    dragStartState = cloneExtrudeDirectionDragState(currentState);
    setCursor(null);
    if (shouldFlushPreview) {
      refreshPreview(true);
    }
  };

  /**
   * 鼠标按下事件。
   *
   * @param event 鼠标事件。
   */
  const handleMouseDown = (event: MouseEvent) => {
    if (!activeTarget) {
      return;
    }
    void startDragFromEvent(event);
  };

  /**
   * 鼠标移动事件。
   *
   * @param event 鼠标事件。
   */
  const handleMouseMove = (event: MouseEvent) => {
    if (!activeTarget) {
      return;
    }
    if (isDragging) {
      updateDragByEvent(event);
      return;
    }
    updateGizmoTransform();
    const hoverOnHandle = resolveGizmoIntersections(event).length > 0;
    setCursor(hoverOnHandle ? 'grab' : null);
  };

  /**
   * 鼠标抬起事件。
   */
  const handleMouseUp = () => {
    if (!isDragging) {
      return;
    }
    stopDrag();
  };

  /**
   * 绑定事件监听。
   */
  const attachListeners = () => {
    if (!activeContainer) {
      return;
    }
    activeContainer.addEventListener('mousedown', handleMouseDown, true);
    activeContainer.addEventListener('mousemove', handleMouseMove, true);
    window.addEventListener('mouseup', handleMouseUp, true);
  };

  /**
   * 解绑事件监听。
   */
  const detachListeners = () => {
    if (!activeContainer) {
      return;
    }
    activeContainer.removeEventListener('mousedown', handleMouseDown, true);
    activeContainer.removeEventListener('mousemove', handleMouseMove, true);
    window.removeEventListener('mouseup', handleMouseUp, true);
  };

  /**
   * 启动手柄控制。
   *
   * @param target 拉伸目标。
   * @param options 启动选项。
   * @returns 是否成功启动。
   */
  const start = (target: ExtrudeTarget, options: StartOptions): boolean => {
    const container = viewportRef.value?.getContainer();
    const camera = viewportRef.value?.getCamera();
    const scene = viewportRef.value?.getScene();
    if (!container || !camera || !scene) {
      message.error('无法启动拉伸手柄');
      return false;
    }

    if (!activeContainer) {
      activeContainer = container;
      activeCamera = camera;
      activeScene = scene;
      previousCursor = container.style.cursor;
      attachListeners();
    } else {
      activeContainer = container;
      activeCamera = camera;
      activeScene = scene;
    }

    activeTarget = target;
    currentState = cloneExtrudeDirectionDragState(options.state);
    ensureGizmo();
    emitStateChange();
    refreshPreview(options.forcePreview ?? true);
    return true;
  };

  /**
   * 更新当前拉伸目标。
   *
   * @param target 拉伸目标。
   * @param options 更新选项。
   * @returns 是否成功。
   */
  const updateTarget = (target: ExtrudeTarget, options: UpdateTargetOptions = {}): boolean => {
    if (!activeTarget) {
      return start(target, {
        state: options.state ?? currentState,
        forcePreview: options.forcePreview,
      });
    }
    activeTarget = target;
    if (options.state) {
      currentState = cloneExtrudeDirectionDragState(options.state);
    }
    updateGizmoTransform();
    refreshPreview(options.forcePreview ?? true);
    return true;
  };

  /**
   * 清理当前选择。
   */
  const clearSelection = () => {
    const wasPreviewSuspended = isDragPreviewSuspended;
    activeTarget = null;
    previewLengths = [0, 0];
    isDragging = false;
    isDragPreviewSuspended = false;
    if (wasPreviewSuspended) {
      onDragPreviewSuspensionChange?.(false);
    }
    dragStartHead = null;
    currentState = {
      direction1Depth: DEFAULT_DEPTH,
      direction1Reverse: false,
      direction2Enabled: false,
      direction2Depth: DEFAULT_DEPTH,
    };
    dragStartState = cloneExtrudeDirectionDragState(currentState);
    disposeGizmo();
    setCursor(null);
  };

  /**
   * 提交当前拉伸。
   *
   * @returns 是否成功提交。
   */
  const submit = async (): Promise<boolean> => {
    if (!activeTarget) {
      return false;
    }
    if (currentState.primaryDepthEditable !== false && currentState.direction1Depth < MIN_DEPTH) {
      message.warning('拉伸深度过小');
      return false;
    }
    await onApply(activeTarget, cloneExtrudeDirectionDragState(currentState));
    onFinish();
    return true;
  };

  /**
   * 停止控制器。
   */
  const stop = () => {
    detachListeners();
    clearSelection();
    activeContainer = null;
    activeCamera = null;
    activeScene = null;
    previousCursor = null;
  };

  return {
    start,
    updateTarget,
    syncState,
    syncPreviewPositions,
    clearSelection,
    submit,
    stop,
  };
}
